import { useState, useEffect } from 'react';
import { Briefcase, TrendingUp, Loader2, AlertCircle } from 'lucide-react';
import { usePortfolio } from '@contexts/PortfolioContext';
import portfolioService from '@services/portfolio.service';

const ProfessionalOpportunities = () => {
  const { industryFocus, experienceLevel, selectedJobs, toggleJob } = usePortfolio();
  
  const [jobProfiles, setJobProfiles] = useState([]);
  const [filteredProfiles, setFilteredProfiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedLevel, setSelectedLevel] = useState('All');

  const levelOptions = ['All', 'Entry Level', 'Mid Level', 'Leadership', 'Executive'];

  // Helper function to match experience level with job profiles
  const matchesExperienceLevel = (jobExperience, userExperience) => {
    if (!userExperience || !jobExperience) return true;

    // Extract max years from user's experience level
    const userMaxYears = {
      '< 3 years': 3,
      '3-5 years': 5,
      '5-10 years': 10,
      '> 10 years': 100
    }[userExperience] || 100;

    // Extract max years from job experience
    const jobExpMatch = jobExperience.match(/(\d+)/g);
    if (!jobExpMatch) return true;
    
    const jobMaxYears = Math.max(...jobExpMatch.map(Number));
    
    // Show jobs that require experience up to user's level
    return jobMaxYears <= userMaxYears;
  };

  useEffect(() => {
    if (industryFocus) {
      fetchJobProfiles();
    } else {
      setJobProfiles([]);
      setFilteredProfiles([]);
    }
  }, [industryFocus]);

  useEffect(() => {
    filterProfiles();
  }, [jobProfiles, selectedLevel, experienceLevel]);

  const fetchJobProfiles = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await portfolioService.getJobProfiles({ 
        industry: industryFocus 
      });
      const profiles = response?.data?.data || [];
      setJobProfiles(Array.isArray(profiles) ? profiles : []);
    } catch (error) {
      console.error('Error fetching job profiles:', error);
      setError('Failed to load job opportunities. Please try again.');
      setJobProfiles([]);
    } finally {
      setLoading(false);
    }
  };

  const filterProfiles = () => {
    let filtered = jobProfiles;

    // Filter by selected level
    if (selectedLevel !== 'All') {
      filtered = filtered.filter(profile => profile.level === selectedLevel);
    }

    // Filter by experience level
    if (experienceLevel) {
      filtered = filtered.filter(profile => 
        matchesExperienceLevel(profile.experience, experienceLevel)
      );
    }

    setFilteredProfiles(filtered);
  };

  const groupedByLevel = filteredProfiles.reduce((acc, profile) => {
    const level = profile.level;
    if (!acc[level]) {
      acc[level] = [];
    }
    acc[level].push(profile);
    return acc;
  }, {});

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="flex items-start gap-2.5 px-3 sm:px-4 py-2.5 border-b border-gray-100 bg-blue-100">
          <div className="w-8 h-8 rounded-lg bg-blue-100 border border-blue-600 flex items-center justify-center flex-shrink-0">
            <TrendingUp className="w-4 h-4 text-blue-600" />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="text-sm sm:text-base font-bold text-gray-900">
              Job Opportunities
            </h3>
            <p className="text-[11px] sm:text-xs text-gray-500 mt-0.5">
              {industryFocus 
                ? `Explore career opportunities in ${industryFocus}${experienceLevel ? ` for ${experienceLevel} experience` : ''}`
                : 'Select an industry focus in the Portfolio tab to view opportunities'}
            </p>
          </div>
        </div>

        {/* Level Filter */}
        {industryFocus && !loading && jobProfiles.length > 0 && (
          <div className="p-3 sm:p-4 border-b border-gray-100">
            <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">
              Filter by Career Level
            </p>
            <div className="flex flex-wrap gap-2">
              {levelOptions.map((level) => {
                const isActive = level === selectedLevel;
                const count = level === 'All' 
                  ? jobProfiles.length 
                  : jobProfiles.filter(p => p.level === level).length;
                
                return (
                  <button
                    key={level}
                    type="button"
                    onClick={() => setSelectedLevel(level)}
                    className={`px-3 py-1.5 rounded-md text-xs sm:text-sm font-semibold transition-all ${
                      isActive
                        ? 'bg-blue-500 text-white shadow-sm'
                        : 'bg-white text-gray-700 border border-gray-200 hover:border-blue-400 hover:text-blue-600'
                    }`}
                  >
                    {level}
                    <span className={`ml-1.5 text-[10px] ${isActive ? 'text-white/80' : 'text-gray-500'}`}>
                      ({count})
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Loading State */}
      {loading && (
        <div className="bg-white border border-gray-200 rounded-xl p-8 text-center">
          <Loader2 className="w-8 h-8 text-blue-500 animate-spin mx-auto mb-3" />
          <p className="text-sm text-gray-600">Loading job opportunities...</p>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-red-900">Error</p>
              <p className="text-sm text-red-700 mt-1">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* No Industry Selected */}
      {!industryFocus && !loading && (
        <div className="bg-gray-50 border border-gray-200 rounded-xl p-8 text-center">
          <Briefcase className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <p className="text-sm font-semibold text-gray-900 mb-1">No Industry Selected</p>
          <p className="text-sm text-gray-600">
            Please select an Industry Focus in the Portfolio tab to view relevant job opportunities.
          </p>
        </div>
      )}

      {/* No Results */}
      {industryFocus && !loading && !error && filteredProfiles.length === 0 && jobProfiles.length > 0 && (
        <div className="bg-gray-50 border border-gray-200 rounded-xl p-8 text-center">
          <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <p className="text-sm font-semibold text-gray-900 mb-1">No Matching Opportunities</p>
          <p className="text-sm text-gray-600">
            No job profiles match your current filters. Try adjusting the career level filter.
          </p>
        </div>
      )}

      {/* Job Profiles Grouped by Level */}
      {!loading && !error && filteredProfiles.length > 0 && (
        <div className="space-y-6">
          {Object.entries(groupedByLevel).map(([level, profiles]) => (
            <div key={level} className="bg-white border border-gray-200 rounded-xl overflow-hidden">
              <div className="px-3 sm:px-4 py-2.5 bg-gray-50 border-b border-gray-100">
                <h4 className="text-sm sm:text-base font-bold text-gray-900">
                  {level} ({profiles.length})
                </h4>
              </div>
              
              <div className="p-3 sm:p-4">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                  {profiles.map((profile) => {
                    const isSelected = selectedJobs.some(j => j.id === profile.id);
                    return (
                    <div
                      key={profile.id}
                      className={`bg-gray-50 border rounded-lg p-3 sm:p-4 transition-all cursor-pointer ${
                        isSelected 
                          ? 'border-blue-500 bg-blue-50/50 shadow-md' 
                          : 'border-gray-200 hover:border-blue-300 hover:shadow-md'
                      }`}
                      onClick={() => toggleJob(profile)}
                    >
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <div className="flex items-start gap-2 flex-1">
                          <div
                            className={`w-5 h-5 rounded flex items-center justify-center flex-shrink-0 mt-0.5 transition-all ${
                              isSelected
                                ? 'bg-blue-500 border border-blue-500'
                                : 'bg-white border border-gray-300'
                            }`}
                          >
                            {isSelected && (
                              <svg
                                className="w-3 h-3 text-white"
                                viewBox="0 0 20 20"
                                fill="currentColor"
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M16.704 5.296a1 1 0 010 1.414l-7.5 7.5a1 1 0 01-1.414 0l-3.5-3.5a1 1 0 011.414-1.414L8.5 12.086l6.79-6.79a1 1 0 011.414 0z"
                                  clipRule="evenodd"
                                />
                              </svg>
                            )}
                          </div>
                          <h5 className="text-sm sm:text-base font-bold text-gray-900 flex-1">
                            {profile.role}
                          </h5>
                        </div>
                        <span className="text-xs font-semibold text-blue-600 bg-blue-50 px-2 py-1 rounded flex-shrink-0">
                          {profile.experience}
                        </span>
                      </div>
                      
                      <div className="space-y-2 text-xs sm:text-sm">
                        <div>
                          <span className="font-semibold text-gray-700">Skills:</span>
                          <span className="text-gray-600 ml-1">{profile.skills}</span>
                        </div>
                        
                        <div>
                          <span className="font-semibold text-gray-700">Tools:</span>
                          <span className="text-gray-600 ml-1">{profile.tools}</span>
                        </div>
                        
                        <div>
                          <span className="font-semibold text-gray-700">Use Case:</span>
                          <span className="text-gray-600 ml-1">{profile.useCase}</span>
                        </div>
                      </div>
                    </div>
                  );
                  })}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Summary */}
      {!loading && !error && filteredProfiles.length > 0 && (
        <div className="space-y-3">
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
            <p className="text-sm text-blue-900">
              <span className="font-semibold">{filteredProfiles.length}</span> job opportunities found
              {experienceLevel && ` matching your experience level (${experienceLevel})`}
            </p>
          </div>
          
          {/* Selected Jobs Summary */}
          {selectedJobs.length > 0 && (
            <div className="bg-green-50 border border-green-200 rounded-xl p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1">
                  <p className="text-sm font-semibold text-green-900 mb-2">
                    {selectedJobs.length} Job Profile{selectedJobs.length > 1 ? 's' : ''} Selected
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {selectedJobs.map((job) => (
                      <span
                        key={job.id}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-white border border-green-300 rounded-full text-xs text-green-800 font-medium"
                      >
                        <Briefcase className="w-3 h-3" />
                        {job.role || job.title}
                      </span>
                    ))}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => selectedJobs.forEach(job => toggleJob(job))}
                  className="text-xs font-semibold text-green-700 hover:text-green-800 underline flex-shrink-0"
                >
                  Clear all
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ProfessionalOpportunities;
